import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useWallet } from '@solana/wallet-adapter-react';
import { soundManager } from '../../utils/sounds';
import { http } from '../../api/http';
import { usePlayerStatsStore } from '../../state/playerStatsStore';

// Types matching server response
interface DailyMission {
  wallet: string;
  missionDate: string;
  missionId: string;
  missionType: string;
  description: string;
  target: number;
  progress: number;
  xpReward: number;
  completed: boolean;
  claimed: boolean;
  handType?: string;
}

interface LoginStreak {
  wallet: string;
  currentStreak: number;
  lastLoginDate: string | null;
  longestStreak: number;
  xpBonusPercent: number;
  streakIncreased?: boolean;
}

interface MissionsResponse {
  missions: DailyMission[];
  loginStreak: LoginStreak;
  resetTime: string;
  totalXpAvailable: number;
  totalXpEarned: number;
}

// Time until reset
function getTimeUntilReset(resetTime: string): string {
  const now = new Date();
  const reset = new Date(resetTime);
  const diff = reset.getTime() - now.getTime();

  if (diff <= 0) return '0:00';

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  return `${hours}:${minutes.toString().padStart(2, '0')}`;
}

// Progress bar component
const ProgressBar: React.FC<{ progress: number; target: number; completed: boolean }> = ({
  progress,
  target,
  completed,
}) => {
  const percent = Math.min(100, (progress / target) * 100);

  return (
    <div className="relative h-2 w-full overflow-hidden rounded-full bg-white/10">
      <motion.div
        className={`absolute inset-y-0 left-0 rounded-full ${
          completed ? 'bg-arcade-green' : 'bg-arcade-cyan'
        }`}
        initial={{ width: 0 }}
        animate={{ width: `${percent}%` }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      />
    </div>
  );
};

// Mission item component with claim button
const MissionItem: React.FC<{
  mission: DailyMission;
  onClaim: (missionId: string) => void;
  claiming: boolean;
  idx: number;
}> = ({ mission, onClaim, claiming, idx }) => {
  const canClaim = mission.completed && !mission.claimed;

  return (
    <motion.div
      key={mission.missionId}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: idx * 0.1 }}
      className={`rounded-xl border p-3 transition ${
        mission.claimed
          ? 'border-arcade-green/50 bg-arcade-green/10'
          : mission.completed
            ? 'border-arcade-yellow/50 bg-arcade-yellow/10'
            : 'border-white/10 bg-white/5'
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <span
          className={`text-sm ${mission.claimed ? 'text-arcade-green line-through' : mission.completed ? 'text-arcade-yellow' : 'text-white/90'}`}
        >
          {mission.description}
        </span>
        <div className="flex items-center gap-2">
          <span
            className={`text-xs font-bold ${mission.claimed ? 'text-arcade-green' : 'text-arcade-yellow'}`}
          >
            +{mission.xpReward} XP
          </span>
          {mission.claimed && <span className="text-arcade-green">✓</span>}
          {canClaim && (
            <button
              onClick={() => onClaim(mission.missionId)}
              disabled={claiming}
              className="arcade-btn px-2 py-1 text-xs disabled:opacity-50"
            >
              {claiming ? '...' : 'CLAIM'}
            </button>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <ProgressBar
          progress={mission.progress}
          target={mission.target}
          completed={mission.completed}
        />
        <span className="text-xs text-white/50 font-mono w-12 text-right">
          {mission.progress}/{mission.target}
        </span>
      </div>
    </motion.div>
  );
};

// Main DailyMissions component
export const DailyMissions: React.FC = () => {
  const { publicKey } = useWallet();
  const wallet = publicKey?.toBase58();

  const [data, setData] = useState<MissionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeUntilReset, setTimeUntilReset] = useState('');
  const [showStreakPopup, setShowStreakPopup] = useState(false);
  const [claiming, setClaiming] = useState<string | null>(null);

  // Fetch missions from server
  const fetchMissions = useCallback(async () => {
    if (!wallet) {
      setLoading(false);
      return;
    }

    try {
      setError(null);
      const response = await http.get<MissionsResponse>(`missions?wallet=${wallet}`);
      setData(response);

      // Show streak popup if streak increased
      if (response.loginStreak.streakIncreased) {
        setShowStreakPopup(true);
        soundManager.play('achievement');
        setTimeout(() => setShowStreakPopup(false), 3000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load missions');
    } finally {
      setLoading(false);
    }
  }, [wallet]);

  // Initial fetch
  useEffect(() => {
    fetchMissions();
  }, [fetchMissions]);

  // Update countdown timer
  useEffect(() => {
    if (!data?.resetTime) return;

    const updateTimer = () => {
      setTimeUntilReset(getTimeUntilReset(data.resetTime));
    };

    updateTimer();
    const interval = setInterval(updateTimer, 60000);

    return () => clearInterval(interval);
  }, [data?.resetTime]);

  // Get fetchStats from player stats store
  const fetchPlayerStats = usePlayerStatsStore((state) => state.fetchStats);

  // Claim mission reward
  const handleClaim = async (missionId: string) => {
    if (!wallet || claiming) return;

    setClaiming(missionId);
    try {
      const result = await http.post<{ success: boolean; xpAwarded?: number; newLevel?: number }>(
        `missions/${missionId}/claim`,
        { wallet },
      );

      if (result.success) {
        soundManager.play('achievement');
        // Refresh missions to get updated state
        await fetchMissions();
        // Also refresh player stats so XP/level updates immediately
        await fetchPlayerStats(wallet);
      }
    } catch (err) {
      console.error('Failed to claim mission:', err);
    } finally {
      setClaiming(null);
    }
  };

  // Show connect wallet message if no wallet
  if (!wallet) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="arcade-panel pixel-corners rounded-2xl p-4 space-y-4"
      >
        <div className="flex items-center gap-2">
          <span className="text-2xl">🎯</span>
          <h2 className="font-display text-sm font-bold neon-text">DAILY MISSIONS</h2>
        </div>
        <div className="text-center py-4 text-white/50 text-sm">
          Connect your wallet to track daily missions
        </div>
      </motion.div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="arcade-panel pixel-corners rounded-2xl p-4"
      >
        <div className="flex items-center gap-2 mb-4">
          <span className="text-2xl">🎯</span>
          <h2 className="font-display text-sm font-bold neon-text">DAILY MISSIONS</h2>
        </div>
        <div className="text-center py-4 text-white/50 text-sm animate-pulse">
          Loading missions...
        </div>
      </motion.div>
    );
  }

  // Error state
  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="arcade-panel pixel-corners rounded-2xl p-4 space-y-4"
      >
        <div className="flex items-center gap-2">
          <span className="text-2xl">🎯</span>
          <h2 className="font-display text-sm font-bold neon-text">DAILY MISSIONS</h2>
        </div>
        <div className="text-center py-4 text-red-400 text-sm">{error}</div>
        <button onClick={fetchMissions} className="arcade-btn w-full py-2 text-xs">
          RETRY
        </button>
      </motion.div>
    );
  }

  if (!data) return null;

  const completedCount = data.missions.filter((m) => m.completed).length;
  const claimedCount = data.missions.filter((m) => m.claimed).length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="arcade-panel pixel-corners rounded-2xl p-4 space-y-4"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-2xl">🎯</span>
          <h2 className="font-display text-sm font-bold neon-text">DAILY MISSIONS</h2>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-white/50">Resets in</span>
          <span className="font-mono text-arcade-cyan">{timeUntilReset}</span>
        </div>
      </div>

      {/* Missions List */}
      <div className="space-y-3">
        {data.missions.map((mission, idx) => (
          <MissionItem
            key={mission.missionId}
            mission={mission}
            onClaim={handleClaim}
            claiming={claiming === mission.missionId}
            idx={idx}
          />
        ))}
      </div>

      {/* Login Streak */}
      <div className="rounded-xl border border-arcade-orange/30 bg-arcade-orange/10 p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xl">🔥</span>
            <div>
              <span className="text-sm font-bold text-arcade-orange">
                LOGIN STREAK: {data.loginStreak.currentStreak} days
              </span>
              {data.loginStreak.xpBonusPercent > 0 && (
                <span className="ml-2 text-xs text-arcade-yellow">
                  +{data.loginStreak.xpBonusPercent}% XP bonus
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-white/40">Best: {data.loginStreak.longestStreak} days</div>
          </div>
        </div>

        {/* Streak milestones hint */}
        {data.loginStreak.currentStreak < 7 && (
          <div className="mt-2 text-xs text-white/40">
            🎁 {7 - data.loginStreak.currentStreak} more days for +25% XP bonus
          </div>
        )}
        {data.loginStreak.currentStreak >= 7 && data.loginStreak.currentStreak < 14 && (
          <div className="mt-2 text-xs text-white/40">
            🎁 {14 - data.loginStreak.currentStreak} more days for +30% XP bonus
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="flex items-center justify-between text-xs text-white/50 pt-2 border-t border-white/10">
        <span>
          {completedCount}/{data.missions.length} completed
        </span>
        <span>
          {claimedCount}/{completedCount} claimed ({data.totalXpEarned} XP)
        </span>
      </div>

      {/* Streak Popup */}
      <AnimatePresence>
        {showStreakPopup && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
          >
            <div className="arcade-panel p-6 text-center space-y-2">
              <div className="text-5xl">🔥</div>
              <div className="font-display text-2xl neon-text">
                {data.loginStreak.currentStreak} DAY STREAK!
              </div>
              <div className="text-arcade-yellow">
                +{data.loginStreak.xpBonusPercent}% XP bonus active
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Legacy export for backwards compatibility - now a no-op since server handles progress
export function updateMissionProgress(
  _type: string,
  _amount: number = 1,
  _handType?: string,
): void {
  // Mission progress is now handled server-side in MatchRoom
  // This function is kept for backwards compatibility but does nothing
}
